import { FC, useEffect } from 'react';
import { useAppSelector } from 'helpers/hooks';
import { getUserData } from 'selectors/user';
import { IEvent } from 'store/eventPage/types';
import { IPage } from 'store/donatePage/types';
import classNames from 'classnames';
import { canShowSosha, getIsStripeDonationFailed } from '../utils';
import { ProfileCard } from 'components/Cards/ProfileCard';
import { Image } from 'components/Image';
import { Sosha } from './Sharing/Sosha';
import { formatNumber, formatNumberAsCurrency, getUrlParams, scrollToTop } from 'helpers/utils';
import { getActionNoun, getActionVerb } from 'helpers/donate';
import { PopupModal } from 'components/PopupModal';
import { ENV_CONFIG } from 'config/environment';
import { CTA } from 'components/ButtonBrand';
import { useShareUrl } from 'hooks/useShareUrl';
import { trackPixel, trackPurchaseOnce } from 'helpers/pixel';
import { Link } from 'components/Link';
import { PaymentIntent } from '@stripe/stripe-js';
import { OathRecurringInterval } from 'store/pay/types';
import { getDisplayTextFromRecurringInterval } from 'screens/Checkout/Form/utils';
import { PaymentInstrument, trackCheckoutConfirmationViewed } from 'helpers/mixpanelEvents';
import { useCompleteSavedCart } from 'hooks/useCompleteSavedCart';

const HIDE_SOSHA = true;

interface Props {
  date: string;
  donationAmount: number;
  donorFullName: string;
  headerBackground?: string;
  hideTip?: boolean;
  isActionRequired: boolean;
  isDonationPending: boolean;
  isDonationSuccessful: boolean;
  isEvent: boolean;
  isRecurring?: boolean;
  numRecipients: number;
  page?: IPage | IEvent;
  pageImage: string;
  paymentIntent?: PaymentIntent;
  paymentMethod?: PaymentInstrument;
  receiptEmail: string | null;
  recurringInterval?: OathRecurringInterval;
  textColor: string;
  tip: number | null;
}

export const Confirmation: FC<Props> = ({
  donationAmount,
  page,
  date,
  donorFullName,
  headerBackground,
  hideTip,
  isActionRequired,
  isDonationPending,
  isDonationSuccessful,
  isEvent,
  isRecurring,
  numRecipients,
  pageImage,
  paymentIntent,
  paymentMethod,
  receiptEmail,
  recurringInterval = 'month',
  textColor,
  tip,
}) => {
  const { urlPartner } = getUrlParams();
  const { user } = useAppSelector(state => ({
    user: getUserData(state),
  }));
  const { handleShare, isPopupOpen } = useShareUrl({ userRefCode: user?.ref_code });
  const isPaymentFailed = !!paymentIntent && getIsStripeDonationFailed(paymentIntent);
  const cartId = useCompleteSavedCart(!isPaymentFailed, { page, paymentProcessor: 'Stripe' });

  useEffect(() => {
    scrollToTop();
    // Nothing re-checks the status after mount, so in-flight donations report now.
    if (!isPaymentFailed) {
      trackPurchaseOnce(paymentIntent?.id, { amount: donationAmount, unit: 'cents' });
    }
    trackPixel('universalPixel');
    trackCheckoutConfirmationViewed({
      is_recurring: !!isRecurring,
      amount: donationAmount,
      client_id: page?.client_id,
      is_stripe: true,
      payment_processor: 'Stripe',
      payment_method: paymentMethod,
      ...(cartId && { cart_id: cartId }),
    });
    // Fire once per view; re-running on prop changes would double-count.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const donationText = getActionNoun(page, urlPartner === 'f4d-nvi' ? 'contribution' : undefined);
  const donateText = getActionVerb(page, urlPartner === 'f4d-nvi' ? 'contribute' : undefined);

  return (
    <div className="confirmation">
      <div className={classNames('confirmation-background-container', { 'bg-ink-blue': isEvent })}>
        <div className="confirmation-container">
          <div className="confirmation-container-left">
            <span className={`eyebrow-text mb5 color-${textColor}`}>
              {donationText}
              {isEvent ? ' / Ticket Purchase' : ''}
              {isDonationPending || isActionRequired
                ? ' pending'
                : isDonationSuccessful
                  ? ' complete'
                  : ' failed'}
            </span>
            <span className={`h4-text mb5 color-${textColor}`}>
              {isDonationPending
                ? `Your ${donationText} is being processed`
                : isActionRequired
                  ? `Your ${donationText} requires additional verification`
                  : isDonationSuccessful
                    ? `Thank you for your ${donationText}!`
                    : `Your ${donationText} could not be processed`}
            </span>
            <span className={`body-text color-${textColor}`}>
              {isDonationPending
                ? 'We are currently processing your payment'
                : isActionRequired
                  ? 'Your payment will be processed after you complete verification steps.'
                  : isDonationSuccessful
                    ? (page as IEvent)?.post_donate_msg ||
                      `Keep the momentum going and invite your friends and family to ${donateText}.`
                    : 'We could not process your payment.'}
            </span>
            {pageImage && (
              <Image className="confirmation-image" alt="Donate page logo" src={pageImage} />
            )}
            {canShowSosha(isDonationPending, urlPartner) && !HIDE_SOSHA ? (
              <Sosha urlPartner={urlPartner} />
            ) : isDonationSuccessful || isDonationPending ? (
              <div className="confirmation-button-row">
                <CTA className="button button-brand" onClick={handleShare}>
                  Share link
                </CTA>
              </div>
            ) : null}
          </div>
          {isDonationPending ? (
            <div className="confirmation-card-container">
              <ProfileCard
                title="Pending Authorization Status"
                align="center"
                smallTitle={true}
                headerBackground={headerBackground}
              >
                <div className="processing-card-content">
                  <Image
                    className="processing-image"
                    src={`${
                      ENV_CONFIG().CLOUD_STORAGE.URL
                    }/static_content/misc_icons/circle_arrows.png`}
                    alt="Circle formed by two arrows"
                  />
                  <span className="body-text">We are currently processing your payment.</span>
                  <br />
                  <span className="body-text">
                    Once received, we will send you an email confirming your {donationText}.
                  </span>
                </div>
              </ProfileCard>
            </div>
          ) : isActionRequired && paymentIntent ? (
            <div className="confirmation-card-container">
              <ProfileCard
                title="Action Required"
                align="center"
                headerBackground={headerBackground}
              >
                <div className="processing-card-content">
                  <Image
                    className="processing-image"
                    src={`${
                      ENV_CONFIG().CLOUD_STORAGE.URL
                    }/static_content/misc_icons/circle_arrows.png`}
                    alt="Circle formed by two arrows"
                  />
                  {paymentIntent.next_action ? (
                    <>
                      <span className="body-text">
                        Your payment requires additional verification. Please click the link below
                        to complete your {donationText}.
                      </span>
                      <br />
                      <CTA
                        className="button button-brand"
                        href={
                          paymentIntent.next_action[paymentIntent.next_action.type]
                            .hosted_verification_url
                        }
                      >
                        Verify Payment
                      </CTA>
                    </>
                  ) : (
                    <span className="body-text">
                      Your payment requires additional verification. Please contact support to
                      complete your {donationText}.
                    </span>
                  )}
                </div>
              </ProfileCard>
            </div>
          ) : isDonationSuccessful ? (
            <div className="confirmation-container-right">
              <div className="confirmation-card-container mb14">
                <ProfileCard title="Thank you!" align="center" headerBackground={headerBackground}>
                  <div className="confirmation-card-content">
                    <span className="body-text mb4">Contribution Summary</span>
                    <div className="display-flex mb13" style={{ justifyContent: 'space-between' }}>
                      <div className="display-grid g3">
                        <span className="h4-text mb2">
                          {formatNumberAsCurrency(donationAmount / 100)}
                        </span>
                        <span className="utility-text">
                          {isRecurring
                            ? `Recurring ${getDisplayTextFromRecurringInterval(
                                recurringInterval
                              )} contribution`
                            : 'One-time contribution'}
                        </span>
                        {!hideTip && (
                          <span className="utility-text">
                            Including {tip !== null && `${formatNumberAsCurrency(tip / 100)} `}tip
                            to Oath
                          </span>
                        )}
                      </div>
                      <div className="display-grid">
                        <span className="h4-text">
                          {formatNumber(numRecipients)} recipient
                          {numRecipients !== 1 && 's'}
                        </span>
                      </div>
                    </div>
                    <div className="display-grid g3" data-dd-privacy="mask">
                      <span className="body-text">{date}</span>
                      <span className="body-text">{donorFullName}</span>
                      <span className="body-text">{receiptEmail}</span>
                    </div>
                  </div>
                </ProfileCard>
              </div>
              <span className="body-text">We’ve emailed you a receipt for your records.</span>
            </div>
          ) : (
            <div className="confirmation-card-container">
              <ProfileCard title="Payment Error" align="center" headerBackground={headerBackground}>
                <div className="error-card-content">
                  <Image
                    className="error-image"
                    src={`${ENV_CONFIG().CLOUD_STORAGE.URL}/static_content/misc_icons/circle_x.png`}
                    alt="Circle with an x in the center"
                  />
                  <span className="body-text">
                    There was an error processing your {donationText}.
                  </span>
                  <br />
                  <span className="body-text mb20">
                    Please{' '}
                    <Link to={`/donate?p=${urlPartner}`}>
                      <span className="body-text">try again</span>
                    </Link>{' '}
                    or use a different payment method.
                  </span>
                  <CTA className="button button-brand" to={`/donate?p=${urlPartner}`}>
                    Try Again
                  </CTA>
                </div>
              </ProfileCard>
            </div>
          )}
          <PopupModal autoClose={true} isSelected={isPopupOpen} title="Copied to Clipboard" />
        </div>
      </div>
    </div>
  );
};
